import type {
    HealthLastDayWelfareDto,
    HealthOverviewDto,
    HealthParameterDto,
    HealthScoreMetricsDto,
    HealthTrendPointDto,
    HealthWoundTrendPointDto,
} from '@/services/types';

import type {
    HealthLastDayWelfare,
    HealthOverviewContext,
    HealthOverviewData,
    HealthOverviewParameter,
    HealthOverviewSummary,
    HealthParameterKey,
    HealthScoreMetrics,
    HealthTrendPoint,
    HealthWoundTrendPoint,
} from './overviewTypes';

function mapScoreMetrics(dto: HealthScoreMetricsDto | undefined | null): HealthScoreMetrics | null {
    if (dto == null) {
        return null;
    }
    return {
        score0: dto.score0 ?? null,
        score1: dto.score1 ?? null,
        score2: dto.score2 ?? null,
        score3: dto.score3 ?? null,
        index: dto.index ?? null,
        slope: dto.slope ?? null,
        count: dto.count ?? null,
    };
}

function mapTrendPoint(dto: HealthTrendPointDto): HealthTrendPoint {
    return {
        day: dto.day,
        score0: dto.score0 ?? null,
        score1: dto.score1 ?? null,
        score2: dto.score2 ?? null,
        score3: dto.score3 ?? null,
        index: dto.index ?? null,
        count: dto.count ?? null,
    };
}

function mapParameter(dto: HealthParameterDto): HealthOverviewParameter {
    return {
        key: dto.key as HealthParameterKey,
        daily: mapScoreMetrics(dto.daily),
        weekly: mapScoreMetrics(dto.weekly),
        trend: dto.trend.map(mapTrendPoint),
        alert: {
            level: dto.alert.level,
            priority: dto.alert.priority,
            appliedThreshold: dto.alert.appliedThreshold ?? null,
        },
        showImageViewer: dto.showImageViewer ?? false,
    };
}

function mapHealthOverviewSummaryDtoToDomain(dto: {
    woundCount?: number | null;
    lastDayWelfare: HealthLastDayWelfareDto;
    woundSlope?: number | null;
    parameters: HealthParameterDto[];
}): HealthOverviewSummary {
    return {
        woundCount: dto.woundCount ?? null,
        lastDayWelfare: mapHealthLastDayWelfareFromApi(dto.lastDayWelfare),
        woundSlope: dto.woundSlope ?? null,
        parameters: dto.parameters.map(mapParameter),
    };
}

export function mapHealthLastDayWelfareFromApi(dto: HealthLastDayWelfareDto): HealthLastDayWelfare {
    return {
        numWounds: dto.numWounds ?? null,
        numHealingWounds: dto.numHealingWounds ?? null,
        anyAcuteWound: dto.anyAcuteWound ?? null,
        noWound: dto.noWound ?? null,
        anyWound: dto.anyWound ?? null,
        oneWound: dto.oneWound ?? null,
        moreThanOneWound: dto.moreThanOneWound ?? null,
        smallWound: dto.smallWound ?? null,
        mediumWound: dto.mediumWound ?? null,
        bigWound: dto.bigWound ?? null,
        smallAcuteWound: dto.smallAcuteWound ?? null,
        mediumAcuteWound: dto.mediumAcuteWound ?? null,
        bigAcuteWound: dto.bigAcuteWound ?? null,
        avgWoundSize: dto.avgWoundSize ?? null,
        avgAcuteWoundSize: dto.avgAcuteWoundSize ?? null,
        woundsArea: dto.woundsArea ?? null,
        acuteWoundsArea: dto.acuteWoundsArea ?? null,
        notSuperior: dto.notSuperior ?? null,
        maturity: dto.maturity ?? null,
        scaleLoss: dto.scaleLoss ?? null,
        startedMaturation: dto.startedMaturation ?? null,
        halfWayMature: dto.halfWayMature ?? null,
        fullyMature: dto.fullyMature ?? null,
    };
}

function mapContext(dto: HealthOverviewDto): HealthOverviewContext {
    return {
        asOf: dto.context.asOf,
        daypartTag: dto.context.daypartTag,
        weeklyWindow: {
            from: dto.context.weeklyWindow.from,
            to: dto.context.weeklyWindow.to,
        },
        trendWindow: {
            from: dto.context.trendWindow.from,
            to: dto.context.trendWindow.to,
        },
    };
}

function mapWoundTrendPoint(dto: HealthWoundTrendPointDto): HealthWoundTrendPoint {
    return {
        day: dto.day,
        woundTypes: {
            numWounds: dto.woundTypes.numWounds ?? null,
            numHealingWounds: dto.woundTypes.numHealingWounds ?? null,
        },
        sizeDistribution: {
            smallWound: dto.sizeDistribution.smallWound ?? null,
            mediumWound: dto.sizeDistribution.mediumWound ?? null,
            bigWound: dto.sizeDistribution.bigWound ?? null,
        },
        distributionOfWounds: {
            moreThanOneWound: dto.distributionOfWounds.moreThanOneWound ?? null,
            oneWound: dto.distributionOfWounds.oneWound ?? null,
        },
        temperature: dto.temperature ?? null,
        woundCount: dto.woundCount ?? null,
        notSuperior: dto.notSuperior ?? null,
    };
}

export function mapHealthOverviewDtoToDomain(dto: HealthOverviewDto): HealthOverviewData {
    return {
        cageId: dto.cageId,
        context: mapContext(dto),
        summary: mapHealthOverviewSummaryDtoToDomain(dto.summary),
        charts: {
            woundDailyTrend: dto.charts.woundDailyTrend.map(mapWoundTrendPoint),
        },
    };
}
